"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.pacsSyncBatchSchema = exports.PACS_SYNC_REJECT_SAMPLE_LIMIT = exports.pacsSyncRejectSchema = exports.pacsSyncRejectReasonSchema = exports.PACS_SYNC_REJECT_REASONS = exports.pacsSyncRejectSourceSchema = exports.PACS_SYNC_REJECT_SOURCES = exports.pacsTimeEntryImportSchema = exports.pacsAssignmentStatusSchema = exports.PACS_ASSIGNMENT_STATUSES = exports.pacsTimeRateImportSchema = exports.pacsStepImportSchema = exports.pacsEmployeeImportSchema = exports.pacsProjectImportSchema = exports.pacsSyncWindowSchema = exports.pacsOptionalSourceIdSchema = exports.pacsSourceIdSchema = void 0;
const zod_1 = require("zod");
const common_js_1 = require("./common.js");
/**
 * Transportfreie Importformen fuer AC-504.
 *
 * Diese Typen sind absichtlich NICHT die DTOs aus `apps/api/src/pacs`:
 * Feldnamen und Verschachtelungen der PACS Web API enden am Mapper. Die
 * numerische PACS-ID bleibt dagegen erhalten, weil sie die stabile Identitaet
 * eines Quelldatensatzes ist. ASAS-interne Datensaetze behalten ihre UUID.
 *
 * SPIEGELN HEISST NICHT NACHSCHAERFEN. Der einzige gemessene Vertrag ueber
 * PACS ist `apps/api/src/pacs/pacs.contract.ts` (181 Projekte, 66 Mitarbeiter,
 * erhoben am 16.09.2026). Diese Datei bildet ihn auf ASAS-Namen ab und darf
 * den Wertebereich dabei NICHT verengen. Der Grund steht im Kopf des
 * Contracts: liegt die Strenge hier, meldet ein Lauf „Abbildung defekt", wo
 * PACS in Wahrheit etwas voellig Zulaessiges geliefert hat — und der
 * Contract-Test, der die Abweichung zeigen soll, bleibt gruen.
 *
 * Deshalb gibt es hier eigene Grundformen statt `textSchema`/`moneySchema`
 * aus `common.ts`: die gelten fuer ASAS-eigene Entitaeten (AC-014/AC-015), wo
 * ein leerer Name tatsaechlich ein Eingabefehler ist. Fuer einen Spiegel
 * fremder Daten waeren sie eine Behauptung ueber PACS.
 */
/**
 * Spiegelt `z.string()` aus dem Contract: KEIN `trim()`, KEIN `min(1)`.
 *
 * `trim()` waere still destruktiv — es veraenderte gespiegelte Quelldaten,
 * ohne dass ein Vergleich mit PACS das je zeigte. `min(1)` waere schlicht
 * falsch: `firstname`, `shortname` und `position` sind in `/employees`
 * gemessen teils leer, und fuer `name` ist nichts Gegenteiliges gemessen.
 */
const pacsMirrorText = zod_1.z.string();
/**
 * Spiegelt `z.string().min(1)` aus dem Contract.
 *
 * Nur fuer die beiden Felder, die dort selbst `min(1)` tragen: `meta_stamp`
 * und `currency.code`. Mehr nicht — jedes weitere `min(1)` waere geraten.
 */
const pacsMirrorRequiredText = zod_1.z.string().min(1);
/**
 * Spiegelt `z.number()` aus dem Contract — insbesondere OHNE `nonnegative()`.
 *
 * `fee_net_before_rebate` ist ein Honorar vor Nachlass. Eine Gutschrift oder
 * ein Storno ist dort negativ, und `DECIMAL(12,2)` nimmt das auch auf. Ein
 * `nonnegative()` haette genau diese Projekte aus dem Import geworfen.
 *
 * `finite()` ist die einzige Ergaenzung und keine Verengung im Betrieb: JSON
 * kann weder `NaN` noch `Infinity` transportieren, ein solcher Wert kaeme also
 * nur aus einem Programmfehler — und zerstoerte den DECIMAL-Cast.
 */
const pacsMirrorAmount = zod_1.z.number().finite();
/**
 * Die eigene Identitaet eines Quelldatensatzes.
 *
 * Hier bleibt `positive()` absichtlich stehen, obwohl der Contract nur
 * `z.number().int()` verlangt: ein Datensatz, dessen EIGENE Id der
 * PACS-Platzhalter `ID_NONE` (-19991001) ist, ist kein Datensatz, sondern ein
 * Dummy. Er als echte Zeile zu speichern, waere schlimmer als ihn stehen zu
 * lassen. Seit dem Quarantaeneweg kostet das auch nicht mehr den ganzen Lauf:
 * so ein Satz wird einzeln aussortiert und gemeldet (`schema_mismatch`).
 */
exports.pacsSourceIdSchema = zod_1.z.number().int().positive();
/**
 * Eine VERWEISENDE PACS-Id, die fehlen darf.
 *
 * PACS besetzt einen fehlenden Verweis mit `ID_NONE` statt mit `null` — belegt
 * am Dummy-Land `-19991001`, das bei 7 von 181 gemessenen Projekten geliefert
 * wird. Der Mapper setzt diesen Platzhalter auf `null` um; das Schema laesst
 * ihn deshalb zu. Ein `positive()` ohne diese Umsetzung haette jeden Datensatz
 * mit fehlendem Verweis verworfen.
 */
exports.pacsOptionalSourceIdSchema = exports.pacsSourceIdSchema.nullable();
exports.pacsSyncWindowSchema = zod_1.z
    .strictObject({
    from: common_js_1.dateSchema,
    to: common_js_1.dateSchema,
})
    .refine(({ from, to }) => from <= to, {
    message: 'Sync window start must not be after its end',
    path: ['to'],
});
exports.pacsProjectImportSchema = zod_1.z.strictObject({
    pacsId: exports.pacsSourceIdSchema,
    number: pacsMirrorText,
    name: pacsMirrorText,
    projectManagerPacsId: exports.pacsOptionalSourceIdSchema,
    clientName: zod_1.z.string().nullable(),
    fee: pacsMirrorAmount,
    currencyCode: pacsMirrorRequiredText,
    metaStamp: pacsMirrorRequiredText,
});
exports.pacsEmployeeImportSchema = zod_1.z.strictObject({
    pacsId: exports.pacsSourceIdSchema,
    name: pacsMirrorText,
    firstname: pacsMirrorText,
    shortname: pacsMirrorText,
    position: pacsMirrorText,
    titleBefore: pacsMirrorText,
    titleAfter: pacsMirrorText,
});
exports.pacsStepImportSchema = zod_1.z.strictObject({
    pacsId: exports.pacsSourceIdSchema,
    projectPacsId: exports.pacsSourceIdSchema,
    name: pacsMirrorText,
    path: pacsMirrorText,
});
exports.pacsTimeRateImportSchema = zod_1.z.strictObject({
    pacsId: exports.pacsSourceIdSchema,
    employeePacsId: exports.pacsSourceIdSchema,
    namePrimary: pacsMirrorText,
    nameSecondary: pacsMirrorText,
    currencyCode: pacsMirrorRequiredText,
    timeUnitPacsId: exports.pacsOptionalSourceIdSchema,
    timeUnitShortname: pacsMirrorText,
});
exports.PACS_ASSIGNMENT_STATUSES = ['without_step', 'unmapped', 'mapped'];
exports.pacsAssignmentStatusSchema = zod_1.z.enum(exports.PACS_ASSIGNMENT_STATUSES);
const pacsLocalDateTimeSchema = zod_1.z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/, 'PACS local datetime expected');
exports.pacsTimeEntryImportSchema = zod_1.z.strictObject({
    pacsId: exports.pacsSourceIdSchema,
    employeePacsId: exports.pacsSourceIdSchema,
    projectPacsId: exports.pacsSourceIdSchema,
    stepPacsId: exports.pacsOptionalSourceIdSchema,
    timeRatePacsId: exports.pacsSourceIdSchema,
    performanceDate: common_js_1.dateSchema,
    startLocal: pacsLocalDateTimeSchema,
    endLocal: pacsLocalDateTimeSchema,
    minutesRendered: zod_1.z.number().finite().nonnegative(),
    minutesChargeable: zod_1.z.number().finite().nonnegative(),
    minutesWorkbreak: zod_1.z.number().int().nonnegative(),
    hours: common_js_1.hoursSchema,
    assignmentStatus: exports.pacsAssignmentStatusSchema,
    metaStamp: pacsMirrorRequiredText,
});
/**
 * Die Quellart eines aussortierten Datensatzes.
 *
 * Steht getrennt vom Grund, damit die Betriebsmeldung „3 Buchungen, 0
 * Projekte" lauten kann statt „3 Datensaetze".
 */
exports.PACS_SYNC_REJECT_SOURCES = [
    'project',
    'employee',
    'step',
    'time_rate',
    'time_entry',
];
exports.pacsSyncRejectSourceSchema = zod_1.z.enum(exports.PACS_SYNC_REJECT_SOURCES);
/**
 * Warum ein einzelner Quelldatensatz nicht uebernommen wurde.
 *
 * Diese Liste ist absichtlich kurz und benennt Ursachen, nicht Symptome —
 * `unknown_project` sagt einem Betreiber etwas, `constraint_violation` nicht.
 *
 *   schema_mismatch          Der Satz passt nicht auf die Importform. Das ist
 *                            der einzige Grund, der auf eine echte
 *                            PACS-Aenderung hindeutet; die uebrigen sind
 *                            normale Zustaende einer fremden Datenbank.
 *   contradictory_duplicate  Dieselbe PACS-Id kam im selben Lauf zweimal mit
 *                            verschiedenem Inhalt. Der erste Satz gewinnt.
 *   outside_window           Das Leistungsdatum liegt ausserhalb des
 *                            angeforderten Fensters. AC-507 hat am 28./30.09.2026
 *                            gemessen, was `/times` als Fenstergrenze versteht:
 *                            `period_start`/`period_end` sind OPTIONAL und
 *                            filtern korrekt, eine einseitige Grenze genuegt.
 *                            Ein Treffer hier ist damit eine Aussage ueber das
 *                            Fenster und kein PACS-Defekt.
 *   unknown_project          Der Satz verweist auf ein Projekt, das dieser
 *   unknown_employee         bzw. einen Mitarbeiter, der dieser Lauf nicht
 *                            gesehen hat. Ein Fremdschluessel mit RESTRICT
 *                            wuerde daran die GANZE Transaktion abbrechen —
 *                            deshalb faengt der Mapper es vorher ab.
 *   pair_query_failed        Die `/times`-Abfrage fuer EIN Paar aus Projekt
 *                            und Person ist gescheitert (AC-508). Als
 *                            einziger Grund kommt er nicht vom Mapper,
 *                            sondern vom Abruf: der Lauf hat fuer dieses Paar
 *                            keine Antwort bekommen und sagt deshalb nichts
 *                            ueber es aus. Das Paar bleibt aus den erfolgreich
 *                            abgefragten Paaren und loest damit KEINE Loeschung
 *                            aus (E-4). Gemessen in AC-508 (M5): bei ~14.400
 *                            Aufrufen je Lauf reicht ein einzelner
 *                            Zeitueberschreiter, um sonst alles abzubrechen.
 */
exports.PACS_SYNC_REJECT_REASONS = [
    'schema_mismatch',
    'contradictory_duplicate',
    'outside_window',
    'unknown_project',
    'unknown_employee',
    'pair_query_failed',
];
exports.pacsSyncRejectReasonSchema = zod_1.z.enum(exports.PACS_SYNC_REJECT_REASONS);
exports.pacsSyncRejectSchema = zod_1.z.strictObject({
    source: exports.pacsSyncRejectSourceSchema,
    /**
     * Bewusst `int()` ohne `positive()`: ein Satz kann gerade deshalb hier
     * stehen, weil seine Id der Platzhalter `ID_NONE` ist. Fehlt die Id in der
     * Antwort ganz, steht `null` — dann ist der Satz ueber `detail` zu finden.
     */
    pacsId: zod_1.z.number().int().nullable(),
    reason: exports.pacsSyncRejectReasonSchema,
    /**
     * Eine Zeile Klartext fuer den Betrieb. KEIN Quelldatensatz: der Grund darf
     * in einem Laufprotokoll stehen, der Inhalt einer fremden Zeitbuchung mit
     * Personenbezug nicht.
     */
    detail: pacsMirrorText,
});
/**
 * Wie viele Aussortierte im Laufprotokoll landen.
 *
 * Der Zaehler bleibt vollstaendig; die Liste ist gedeckelt, damit ein
 * Erstimport mit zehntausend Fremdverweisen keine unbegrenzt wachsende Zeile
 * schreibt. Wer alle sehen will, liest das Anwendungslog.
 */
exports.PACS_SYNC_REJECT_SAMPLE_LIMIT = 50;
exports.pacsSyncBatchSchema = zod_1.z.strictObject({
    window: exports.pacsSyncWindowSchema,
    projects: zod_1.z.array(exports.pacsProjectImportSchema),
    employees: zod_1.z.array(exports.pacsEmployeeImportSchema),
    steps: zod_1.z.array(exports.pacsStepImportSchema),
    timeRates: zod_1.z.array(exports.pacsTimeRateImportSchema),
    timeEntries: zod_1.z.array(exports.pacsTimeEntryImportSchema),
    rejects: zod_1.z.array(exports.pacsSyncRejectSchema),
});
//# sourceMappingURL=pacs-sync.js.map